import { useEffect } from 'react'
import { useRouterState } from '@tanstack/react-router'
import i18n from '@/modules/i18n/i18n'
import { applyDocumentDirection } from '@/modules/i18n/useTar'
import { isPrerenderedPath, parseLangPrefix } from '@/modules/seo/prerenderedPaths'
import { getEffectiveUiLanguage } from '@/modules/i18n/uiLanguageVisibility'
import type { SupportedLanguage } from '@/modules/i18n/supportedLanguages'

/**
 * Reconciles the runtime i18n language with the current URL and localStorage.
 *
 * Rules (URL is authoritative on prerendered paths):
 *  - Prefixed prerendered URL (/he/foo, /de/foo) → runtime tracks the URL.
 *  - Canonical prerendered URL (/foo with no prefix) → runtime is 'en'.
 *    Do not consult LS here — the prerendered HTML is English, and reading
 *    LS would flicker English content into Hebrew on refresh. Hebrew users
 *    are routed to /he/foo via <LocalizedLink> for internal navigation.
 *  - Non-prerendered route → runtime tracks LS (or 'en' if LS empty).
 *
 * Implemented as a single global subscriber rather than per-layout effects
 * so that leaving the $lang subtree (e.g. /de/faq → /about) reverts runtime
 * language correctly without needing layout cleanup logic.
 */
export function LanguageSync() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  useEffect(() => {
    const prefix = parseLangPrefix(pathname)
    let desired: SupportedLanguage
    if (prefix && isPrerenderedPath(pathname)) {
      desired = prefix.lang
    } else if (isPrerenderedPath(pathname)) {
      desired = 'en'
    } else {
      desired = getEffectiveUiLanguage()
    }

    if (i18n.language !== desired) {
      void i18n.changeLanguage(desired).then(() => {
        applyDocumentDirection(desired)
      })
    }
  }, [pathname])

  return null
}
